/**
 * Named map of semantic tokens for the Galaxy map's per-object-type markers.
 *
 * The 25-odd raw palette colours GalaxyPage once scattered across its
 * PositionTile and legend are defined HERE as a single source of truth, and
 * the legend reads from this same map — so a marker and its legend swatch can
 * never drift apart.
 *
 * These are the only raw-palette survivors allowed outside a page root's
 * `min-h-screen bg-space-900` (see DS-21 house rules). Team-planet violet has
 * no status-token equivalent, so it stays as a literal here.
 */
export type GalaxyMarkerType = 'ownPlanet' | 'teamPlanet' | 'other' | 'empty' | 'asteroid' | 'sun';

export interface GalaxyMarkerStyle {
    /** Border class for the marker disc. */
    border: string;
    /** Border style class — `border-dashed` for empty, `border` otherwise. */
    borderStyle: string;
    /** Optional glow shadow class. */
    glow?: string;
    /** Optional gradient class for the sun disc background. */
    gradient?: string;
    /** Text colour class for the marker's label. */
    text: string;
    /** Background class for the small team-planet badge (teamPlanet only). */
    badge?: string;
    /** Legend label. */
    label: string;
}

export const GALAXY_MARKERS: Record<GalaxyMarkerType, GalaxyMarkerStyle> = {
    ownPlanet: {
        border: 'border-status-success',
        borderStyle: 'border',
        glow: 'shadow-[0_0_10px_rgba(52,211,153,0.7)]',
        text: 'text-status-success',
        label: 'Your planet',
    },
    teamPlanet: {
        border: 'border-violet-400',
        borderStyle: 'border',
        glow: 'shadow-[0_0_10px_rgba(167,139,250,0.7)]',
        text: 'text-violet-300',
        badge: 'bg-violet-500',
        label: 'Team planet',
    },
    other: {
        border: 'border-space-500',
        borderStyle: 'border',
        text: 'text-content-secondary',
        label: 'Other planet',
    },
    empty: {
        border: 'border-space-600',
        borderStyle: 'border border-dashed',
        text: 'text-content-muted',
        label: 'Empty',
    },
    asteroid: {
        border: 'border-space-600',
        borderStyle: 'border',
        text: 'text-content-muted',
        label: 'Asteroid',
    },
    sun: {
        border: 'border-space-700',
        borderStyle: 'border-2',
        gradient: 'bg-gradient-to-br from-rose-500 to-orange-500',
        text: 'text-content-primary',
        label: 'Sun',
    },
};

/** The legend entries, derived from the marker map so they can never drift. */
export const GALAXY_LEGEND_ENTRIES: { type: GalaxyMarkerType }[] = [
    { type: 'ownPlanet' },
    { type: 'teamPlanet' },
    { type: 'empty' },
    { type: 'asteroid' },
];
